/**
 * Procedural parallax backdrops: a sky (gradient, stars or the like, and a sun, moon or
 * planet) and two silhouette layers (mid and near), each moving at its own parallax.
 *
 * Every layer is drawn once into a canvas at exactly the device resolution it is shown at
 * (deterministic per theme) and copied with drawImage at whole device pixels each frame:
 * 1:1 copies without any scaling or filtering are the cheapest thing a canvas can do, also
 * without a GPU. Nothing below the floor line is drawn (the ground covers it), and the
 * silhouettes only where they are visible. Rendered layers are kept in a small LRU cache.
 */
import { Rng } from '@cube-rush/shared';
import { createLayer, LayerPool } from './canvas';
import { mix, rgba, shade } from './color';
import { seasonArt } from './seasonBackdrop';
import type { BackdropKind, Theme } from './themes';
import type { ViewTransform } from './view';

type LayerId = 'mid' | 'near';

interface LayerSpec {
  /** Strip height as a fraction of the view height. */
  height: number;
  /** Width / height of a strip tile. */
  aspect: number;
  /** Horizontal parallax (fraction of the camera movement). */
  parallax: number;
  /** Vertical parallax (fraction of the floor's movement on screen). */
  parallaxY: number;
}

/** Silhouette layers, far to near (a constant: the frame loop allocates nothing). */
const LAYER_IDS: readonly LayerId[] = ['mid', 'near'];

const LAYERS: Readonly<Record<LayerId, LayerSpec>> = {
  mid: { height: 0.52, aspect: 2.6, parallax: 0.15, parallaxY: 0.3 },
  near: { height: 0.3, aspect: 3.2, parallax: 0.32, parallaxY: 0.5 },
};

/** The sky (with its stars and celestial body) drifts very slowly. */
const SKY_PARALLAX = 0.03;
/** Where the floor sits on screen when the camera rests (fraction of the view height). */
const REST_FLOOR = 0.82;
/** Speed of the falling digital rain (theme "rain"), CSS px per second. */
const RAIN_SPEED = 90;

type TilePainter = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  t: Theme,
) => void;

interface BackdropArt {
  /** Stars and the like, painted over the sky gradient. */
  far: TilePainter;
  mid: TilePainter;
  near: TilePainter;
  celestial?: 'sun' | 'moon' | 'planet' | 'glow';
  /** Digital rain falls over the sky (drawn live, it moves). */
  falling?: boolean;
}

/**
 * Rendered layers, shared by the views of one size and theme; a view gives its layers back
 * when its size or theme changes and when it goes away (Backdrop.dispose).
 */
export const backdropLayers = new LayerPool(4);

/** The layer a view uses per slot: no cache-key strings in the per-frame path. */
interface Memo {
  /** Key in the pool (null: none held). */
  key: string | null;
  theme: Theme | null;
  w: number;
  h: number;
  canvas: HTMLCanvasElement | null;
}

type Slot = 'sky' | LayerId;

const SLOTS: readonly Slot[] = ['sky', 'mid', 'near'];

function memo(): Memo {
  return { key: null, theme: null, w: 0, h: 0, canvas: null };
}

/** Non-negative remainder. */
function mod(value: number, m: number): number {
  return ((value % m) + m) % m;
}

export class Backdrop {
  private readonly memo: Record<Slot, Memo> = { sky: memo(), mid: memo(), near: memo() };

  constructor(private readonly pool: LayerPool = backdropLayers) {}

  /** Gives the layers back to the pool (the view is gone). Drawing again takes new ones. */
  dispose(): void {
    for (const slot of SLOTS) {
      const m = this.memo[slot];
      if (m.key !== null) this.pool.release(m.key);
      this.memo[slot] = memo();
    }
  }

  /** Draws the backdrop of `theme` into the view (CSS px, context scaled by `scale`). */
  draw(
    ctx: CanvasRenderingContext2D,
    theme: Theme,
    view: ViewTransform,
    scale: number,
    time: number,
  ): void {
    const art = ART[theme.backdrop];
    // Device-pixel frame of the view; everything below the floor line is ground.
    const left = Math.round(view.x * scale);
    const top = Math.round(view.y * scale);
    const right = Math.round((view.x + view.w) * scale);
    const bottom = Math.round((view.y + view.h) * scale);
    const floor = Math.min(bottom, Math.round(view.sy(0) * scale));
    if (floor <= top) return;
    const width = right - left;
    const height = bottom - top;

    const sky = this.layer('sky', theme, width, height, () => drawSky(art, theme, width, height));
    if (sky) {
      const shift = mod(Math.round(view.left * view.ppb * scale * SKY_PARALLAX), width);
      blit(ctx, scale, sky, shift, 0, width - shift, floor - top, left, top);
      if (shift > 0) blit(ctx, scale, sky, 0, 0, shift, floor - top, right - shift, top);
    } else {
      ctx.fillStyle = theme.sky[1];
      ctx.fillRect(view.x, view.y, view.w, view.h);
    }
    if (art.falling) drawRain(ctx, view, time, theme, floor / scale);

    const rest = top + height * REST_FLOOR;
    for (const id of LAYER_IDS) {
      const spec = LAYERS[id];
      const tileH = Math.max(1, Math.round(height * spec.height));
      const tileW = Math.max(1, Math.round(tileH * spec.aspect));
      const base = Math.round(rest + (view.sy(0) * scale - rest) * spec.parallaxY);
      const tileTop = base - tileH;
      const from = Math.max(top, tileTop);
      const to = Math.min(floor, base);
      if (to > from) {
        const tile = this.layer(id, theme, tileW, tileH, () =>
          drawTile(art[id], theme, id, tileW, tileH),
        );
        if (tile) {
          const shift = mod(Math.round(view.left * view.ppb * scale * spec.parallax), tileW);
          for (let x = left - shift; x < right; x += tileW) {
            const sx = Math.max(0, left - x);
            const sw = Math.min(tileW, right - x) - sx;
            if (sw > 0) blit(ctx, scale, tile, sx, from - tileTop, sw, to - from, x + sx, from);
          }
        }
      }
      // Below the strip the layer continues as its solid colour (the camera rose).
      if (base < floor) {
        ctx.fillStyle = id === 'mid' ? theme.mid : theme.near;
        ctx.fillRect(view.x, base / scale, view.w, (floor - base) / scale);
      }
    }
  }

  /** The rendered layer for `slot` at w × h device px (drawn and cached on first use). */
  private layer(
    slot: Slot,
    theme: Theme,
    w: number,
    h: number,
    create: () => HTMLCanvasElement | null,
  ): HTMLCanvasElement | null {
    const m = this.memo[slot];
    if (m.theme !== theme || m.w !== w || m.h !== h) {
      const key = `${theme.name}:${slot}:${w}x${h}`;
      // The old layer goes back to the pool (a spare for a while: views switch back and forth).
      const canvas = this.pool.acquire(key, create);
      if (m.key !== null) this.pool.release(m.key);
      m.key = key;
      m.theme = theme;
      m.w = w;
      m.h = h;
      m.canvas = canvas;
    }
    return m.canvas;
  }
}

/** Copies a source rectangle 1:1 to device pixel (dx, dy) on a context scaled by `scale`. */
function blit(
  ctx: CanvasRenderingContext2D,
  scale: number,
  image: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
): void {
  if (sw <= 0 || sh <= 0) return;
  ctx.drawImage(image, sx, sy, sw, sh, dx / scale, dy / scale, sw / scale, sh / scale);
}

/** Falling columns of glowing squares (theme "rain"), drawn live above the sky. */
function drawRain(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  time: number,
  theme: Theme,
  floor: number,
): void {
  const cell = Math.max(4, view.h / 70);
  const spacing = cell * 3.1;
  const columns = Math.ceil(view.w / spacing);
  const drift = mod(view.left * view.ppb * 0.08, spacing);
  const span = floor - view.y;
  for (let c = 0; c <= columns; c++) {
    const lane = (c * 0.618034) % 1;
    if (lane < 0.35) continue;
    const x = view.x + c * spacing - drift;
    const length = cell * (5 + Math.floor(lane * 9));
    const speed = RAIN_SPEED * (0.6 + lane * 0.8);
    const head = view.y + mod(time * speed + lane * 997, span + length) - length * 0.2;
    ctx.fillStyle = rgba(theme.glow, 0.16);
    ctx.fillRect(x, head - length, cell, length);
    ctx.fillStyle = rgba(theme.glow, 0.32);
    ctx.fillRect(x, head - length * 0.45, cell, length * 0.45);
    ctx.fillStyle = 'rgba(234, 255, 239, 0.75)';
    ctx.fillRect(x, head - cell, cell, cell);
  }
}

// ---- layers ----

/** Sky gradient, the far layer (stars, sparkles, ...) and the celestial body, in one canvas. */
function drawSky(art: BackdropArt, theme: Theme, w: number, h: number): HTMLCanvasElement | null {
  const layer = createLayer(w, h);
  if (!layer) return null;
  const { ctx } = layer;
  const gradient = ctx.createLinearGradient(0, 0, 0, h);
  gradient.addColorStop(0, theme.sky[0]);
  gradient.addColorStop(0.55, theme.sky[1]);
  gradient.addColorStop(1, theme.sky[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
  art.far(ctx, w, h, new Rng(seedOf(theme, 'far')), theme);
  // A holiday may bring its own sky body (Halloween's big moon); a glow on the horizon stays.
  const holiday = seasonArt(theme)?.celestial;
  if (art.celestial === 'glow' || (art.celestial && !holiday)) {
    drawCelestial(ctx, art.celestial, theme, w, h);
  }
  holiday?.(ctx, theme, w, h);
  return layer.canvas;
}

function seedOf(theme: Theme, layer: string): number {
  // A holiday version ("forest~winter") keeps the theme's own scenery under its decorations.
  const name = theme.name.split('~')[0];
  return [...`${name}:${layer}`].reduce((s, c) => (s * 31 + c.charCodeAt(0)) >>> 0, 7);
}

function drawTile(
  painter: TilePainter,
  theme: Theme,
  id: LayerId,
  w: number,
  h: number,
): HTMLCanvasElement | null {
  const layer = createLayer(w, h);
  if (!layer) return null;
  painter(layer.ctx, w, h, new Rng(seedOf(theme, id)), theme);
  // Holiday art over the theme's own silhouettes (theme.season, see seasonBackdrop.ts).
  seasonArt(theme)?.[id]?.(layer.ctx, w, h, new Rng(seedOf(theme, `${id}+season`)), theme);
  return layer.canvas;
}

/** Calls `draw` at x and at x ± w so shapes crossing an edge wrap around (seamless tiles). */
function wrapped(w: number, x: number, extent: number, draw: (x: number) => void): void {
  draw(x);
  if (x - extent < 0) draw(x + w);
  if (x + extent > w) draw(x - w);
}

// ---- layer painters ----

function stars(density: number, sizeScale = 1): TilePainter {
  return (ctx, w, h, rng, t) => {
    const count = Math.round(((w * h) / 9000) * density);
    const unit = Math.max(1, h / 420) * sizeScale;
    for (let i = 0; i < count; i++) {
      const x = rng.range(0, w);
      // More stars high in the sky.
      const y = h * rng.next() ** 1.6 * 0.85;
      const big = rng.chance(0.08);
      const r = (big ? rng.range(1.4, 2.2) : rng.range(0.5, 1.2)) * unit;
      ctx.fillStyle = rgba(rng.chance(0.25) ? t.glow : '#ffffff', rng.range(0.35, 0.95));
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      if (big) {
        ctx.strokeStyle = rgba('#ffffff', 0.35);
        ctx.lineWidth = Math.max(1, unit * 0.6);
        ctx.beginPath();
        ctx.moveTo(x - r * 3, y);
        ctx.lineTo(x + r * 3, y);
        ctx.moveTo(x, y - r * 3);
        ctx.lineTo(x, y + r * 3);
        ctx.stroke();
      }
    }
  };
}

/** A periodic ridge line (mountains, hills) filled down to the strip bottom. */
function ridge(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  options: {
    color: string;
    peaks: number;
    min: number;
    max: number;
    sharp: boolean;
    rim?: string;
    snow?: string;
  },
): void {
  const { peaks, min, max } = options;
  const heights: number[] = [];
  for (let i = 0; i < peaks; i++) heights.push(rng.range(min, max));
  const point = (i: number) => {
    const step = w / peaks;
    const x = i * step + (i % 2 === 0 ? 0 : step * 0.15);
    const y = h * (1 - heights[((i % peaks) + peaks) % peaks]!);
    return [x, y] as const;
  };
  ctx.beginPath();
  ctx.moveTo(0, h);
  const first = point(0);
  ctx.lineTo(0, first[1]);
  for (let i = 1; i <= peaks; i++) {
    const [x, y] = point(i);
    if (options.sharp) {
      ctx.lineTo(x, y);
    } else {
      const [px, py] = point(i - 1);
      ctx.quadraticCurveTo((px + x) / 2, Math.min(py, y) - h * 0.05, x, y);
    }
  }
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fillStyle = options.color;
  ctx.fill();
  if (options.snow) {
    for (let i = 0; i < peaks; i++) {
      const [x, y] = point(i);
      if (heights[i]! < (min + max) / 2) continue;
      const size = h * 0.07;
      ctx.fillStyle = options.snow;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + size * 0.9, y + size);
      ctx.lineTo(x + size * 0.3, y + size * 0.8);
      ctx.lineTo(x, y + size * 1.2);
      ctx.lineTo(x - size * 0.3, y + size * 0.8);
      ctx.lineTo(x - size * 0.9, y + size);
      ctx.closePath();
      ctx.fill();
    }
  }
  if (options.rim) {
    ctx.beginPath();
    ctx.moveTo(0, first[1]);
    for (let i = 1; i <= peaks; i++) {
      const [x, y] = point(i);
      ctx.lineTo(x, y);
    }
    ctx.strokeStyle = options.rim;
    ctx.lineWidth = Math.max(1.5, h / 160);
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

function skyline(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  color: string,
  windows: string | null,
  density: number,
): void {
  let x = 0;
  while (x < w) {
    const bw = rng.range(0.035, 0.08) * w;
    const bh = rng.range(0.3, 0.95) * h;
    const width = Math.min(bw, w - x);
    ctx.fillStyle = color;
    ctx.fillRect(x, h - bh, width, bh);
    if (rng.chance(0.3)) {
      // Antenna or stepped roof.
      ctx.fillRect(x + width * 0.4, h - bh - h * 0.06, width * 0.2, h * 0.06);
    }
    if (windows) {
      const cell = Math.max(3, h * 0.035);
      for (let wy = h - bh + cell; wy < h - cell; wy += cell * 1.8) {
        for (let wx = x + cell * 0.6; wx < x + width - cell; wx += cell * 1.6) {
          if (!rng.chance(density)) continue;
          ctx.fillStyle = rgba(windows, rng.range(0.45, 0.95));
          ctx.fillRect(wx, wy, cell * 0.8, cell * 0.9);
        }
      }
    }
    x += width + rng.range(0, 0.012) * w;
  }
}

function waveBands(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  color: string,
  crest: string,
  rows: number,
): void {
  for (let row = 0; row < rows; row++) {
    const top = h * (0.25 + (row / rows) * 0.7);
    const cycles = 3 + row * 2;
    const amplitude = h * (0.04 + row * 0.01);
    const phase = rng.range(0, Math.PI * 2);
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let i = 0; i <= 96; i++) {
      const x = (i / 96) * w;
      ctx.lineTo(x, top + Math.sin((i / 96) * Math.PI * 2 * cycles + phase) * amplitude);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fillStyle = shade(color, -row * 0.12);
    ctx.fill();
    ctx.beginPath();
    for (let i = 0; i <= 96; i++) {
      const x = (i / 96) * w;
      const y = top + Math.sin((i / 96) * Math.PI * 2 * cycles + phase) * amplitude;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = rgba(crest, 0.5 - row * 0.1);
    ctx.lineWidth = Math.max(1.5, h / 120);
    ctx.stroke();
  }
}

function puffs(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  color: string,
  count: number,
  size: number,
  highlight: string,
): void {
  for (let i = 0; i < count; i++) {
    const cx = rng.range(0, w);
    const cy = h * rng.range(0.45, 0.85);
    const r = h * size * rng.range(0.7, 1.2);
    wrapped(w, cx, r * 2.4, (x) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x - r * 1.1, cy + r * 0.25, r * 0.75, 0, Math.PI * 2);
      ctx.arc(x, cy, r, 0, Math.PI * 2);
      ctx.arc(x + r * 1.15, cy + r * 0.2, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(x - r * 1.8, cy + r * 0.25, r * 3.7, h - cy);
      ctx.fillStyle = rgba(highlight, 0.18);
      ctx.beginPath();
      ctx.arc(x - r * 0.25, cy - r * 0.3, r * 0.45, 0, Math.PI * 2);
      ctx.fill();
    });
  }
}

function shards(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  color: string,
  edge: string,
  count: number,
  tall: number,
): void {
  for (let i = 0; i < count; i++) {
    const cx = rng.range(0, w);
    const sh = h * rng.range(0.35, 1) * tall;
    const sw = sh * rng.range(0.18, 0.3);
    const lean = rng.range(-0.15, 0.15) * sh;
    wrapped(w, cx, sw + Math.abs(lean), (x) => {
      ctx.beginPath();
      ctx.moveTo(x - sw / 2, h);
      ctx.lineTo(x - sw / 2 + lean * 0.3, h - sh * 0.75);
      ctx.lineTo(x + lean, h - sh);
      ctx.lineTo(x + sw / 2 + lean * 0.3, h - sh * 0.7);
      ctx.lineTo(x + sw / 2, h);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = rgba(edge, 0.55);
      ctx.lineWidth = Math.max(1, h / 200);
      ctx.beginPath();
      ctx.moveTo(x + lean, h - sh);
      ctx.lineTo(x + lean * 0.2, h - sh * 0.2);
      ctx.stroke();
    });
  }
}

function trees(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  color: string,
  pine: boolean,
  count: number,
): void {
  for (let i = 0; i < count; i++) {
    const cx = rng.range(0, w);
    const th = h * rng.range(0.45, 0.95);
    const tw = th * (pine ? 0.38 : 0.55);
    wrapped(w, cx, tw, (x) => {
      ctx.fillStyle = color;
      if (pine) {
        for (let tier = 0; tier < 3; tier++) {
          const y0 = h - th * (0.25 + tier * 0.25);
          const width = tw * (1 - tier * 0.22);
          ctx.beginPath();
          ctx.moveTo(x - width / 2, y0);
          ctx.lineTo(x, y0 - th * 0.38);
          ctx.lineTo(x + width / 2, y0);
          ctx.closePath();
          ctx.fill();
        }
        ctx.fillRect(x - tw * 0.06, h - th * 0.3, tw * 0.12, th * 0.3);
      } else {
        ctx.beginPath();
        ctx.arc(x, h - th * 0.62, tw / 2, 0, Math.PI * 2);
        ctx.arc(x - tw * 0.3, h - th * 0.45, tw * 0.35, 0, Math.PI * 2);
        ctx.arc(x + tw * 0.32, h - th * 0.48, tw * 0.33, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(x - tw * 0.07, h - th * 0.4, tw * 0.14, th * 0.4);
      }
    });
  }
}

function volcanoes(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  const count = 3;
  const body = ctx.createLinearGradient(0, h * 0.1, 0, h);
  body.addColorStop(0, shade(t.mid, 0.12));
  body.addColorStop(1, shade(t.near, -0.2));
  const width = Math.max(1.5, h / 160);
  for (let i = 0; i < count; i++) {
    const cx = (i + rng.range(0.2, 0.8)) * (w / count);
    // Low enough that the crater glow and the smoke stay inside the strip.
    const vh = h * rng.range(0.4, 0.62);
    const base = vh * 1.6;
    const crater = vh * 0.16;
    const top = h - vh;
    wrapped(w, cx, base, (x) => {
      // Cone with a faint hot rim.
      ctx.beginPath();
      ctx.moveTo(x - base, h);
      ctx.lineTo(x - crater, top);
      ctx.lineTo(x + crater, top);
      ctx.lineTo(x + base, h);
      ctx.closePath();
      ctx.fillStyle = body;
      ctx.fill();
      ctx.strokeStyle = rgba('#ff7a1a', 0.35);
      ctx.lineWidth = width;
      ctx.lineJoin = 'round';
      ctx.stroke();
      // Lava pouring from the crater: a soft glow, then the bright core.
      ctx.beginPath();
      ctx.moveTo(x - crater * 0.4, top);
      ctx.lineTo(x - crater * 0.9, top + vh * 0.3);
      ctx.lineTo(x - crater * 0.6, top + vh * 0.55);
      ctx.lineTo(x - crater * 1.4, top + vh * 0.85);
      ctx.moveTo(x + crater * 0.3, top);
      ctx.lineTo(x + crater * 1.1, top + vh * 0.4);
      ctx.lineTo(x + crater * 0.8, top + vh * 0.7);
      ctx.strokeStyle = rgba('#ff5a1f', 0.35);
      ctx.lineWidth = width * 4;
      ctx.stroke();
      ctx.strokeStyle = '#ffb238';
      ctx.lineWidth = width * 1.4;
      ctx.stroke();
      // Glowing crater and a puff of smoke.
      const glow = ctx.createRadialGradient(x, top, 0, x, top, crater * 2.2);
      glow.addColorStop(0, rgba('#ffd27a', 0.95));
      glow.addColorStop(0.35, rgba('#ff7a1a', 0.55));
      glow.addColorStop(1, rgba('#ff5a1f', 0));
      ctx.fillStyle = glow;
      ctx.fillRect(x - crater * 2.2, top - crater * 2.2, crater * 4.4, crater * 4.4);
      ctx.fillStyle = rgba(t.near, 0.5);
      for (let k = 0; k < 3; k++) {
        ctx.beginPath();
        ctx.arc(
          x + (k - 1) * crater * 0.8,
          top - crater * (1.2 + k * 0.5),
          crater * (0.5 + k * 0.2),
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    });
  }
}

function embers(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  stars(0.5)(ctx, w, h, rng, t);
  for (let i = 0; i < 26; i++) {
    const x = rng.range(0, w);
    const y = h * rng.range(0.3, 0.95);
    const r = Math.max(1, h / 400) * rng.range(1, 2.4);
    ctx.fillStyle = rgba(rng.chance(0.5) ? '#ffb238' : '#ff5a1f', rng.range(0.4, 0.9));
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function aurora(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  stars(0.9)(ctx, w, h, rng, t);
  for (let band = 0; band < 2; band++) {
    const top = h * (0.12 + band * 0.12);
    const cycles = 2 + band;
    ctx.beginPath();
    for (let i = 0; i <= 64; i++) {
      const x = (i / 64) * w;
      const y = top + Math.sin((i / 64) * Math.PI * 2 * cycles + band) * h * 0.05;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = rgba(band === 0 ? '#5dffc2' : '#8fd8ff', 0.16);
    ctx.lineWidth = h * 0.06;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.strokeStyle = rgba('#d8fff0', 0.2);
    ctx.lineWidth = Math.max(1, h / 300);
    ctx.stroke();
  }
}

function sparkles(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  stars(0.6, 1.3)(ctx, w, h, rng, t);
  for (let i = 0; i < 18; i++) {
    const x = rng.range(0, w);
    const y = h * rng.range(0.05, 0.6);
    const r = Math.max(2, h / 140) * rng.range(1, 1.8);
    ctx.fillStyle = rgba('#ffffff', rng.range(0.4, 0.8));
    ctx.beginPath();
    ctx.moveTo(x, y - r * 2);
    ctx.lineTo(x + r * 0.45, y - r * 0.45);
    ctx.lineTo(x + r * 2, y);
    ctx.lineTo(x + r * 0.45, y + r * 0.45);
    ctx.lineTo(x, y + r * 2);
    ctx.lineTo(x - r * 0.45, y + r * 0.45);
    ctx.lineTo(x - r * 2, y);
    ctx.lineTo(x - r * 0.45, y - r * 0.45);
    ctx.closePath();
    ctx.fill();
  }
}

function fireflies(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  stars(0.7)(ctx, w, h, rng, t);
  for (let i = 0; i < 30; i++) {
    const x = rng.range(0, w);
    const y = h * rng.range(0.55, 0.95);
    const r = Math.max(1.5, h / 300) * rng.range(1, 1.6);
    ctx.fillStyle = rgba(t.glow, 0.18);
    ctx.beginPath();
    ctx.arc(x, y, r * 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(t.glow, 0.9);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function craters(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  ridge(ctx, w, h, rng, { color: t.near, peaks: 7, min: 0.25, max: 0.5, sharp: false });
  for (let i = 0; i < 14; i++) {
    const x = rng.range(0, w);
    const y = h * rng.range(0.7, 0.95);
    const r = h * rng.range(0.03, 0.08);
    wrapped(w, x, r, (cx) => {
      ctx.fillStyle = shade(t.near, -0.35);
      ctx.beginPath();
      ctx.ellipse(cx, y, r, r * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }
}

function asteroids(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  for (let i = 0; i < 9; i++) {
    const x = rng.range(0, w);
    const y = h * rng.range(0.1, 0.75);
    const r = h * rng.range(0.025, 0.07);
    wrapped(w, x, r, (cx) => {
      ctx.fillStyle = t.far;
      ctx.beginPath();
      for (let k = 0; k < 7; k++) {
        const a = (k / 7) * Math.PI * 2;
        const rr = r * (0.75 + ((k * 37) % 5) * 0.08);
        if (k === 0) ctx.moveTo(cx + Math.cos(a) * rr, y + Math.sin(a) * rr);
        else ctx.lineTo(cx + Math.cos(a) * rr, y + Math.sin(a) * rr);
      }
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = rgba('#ffffff', 0.12);
      ctx.beginPath();
      ctx.arc(cx - r * 0.25, y - r * 0.25, r * 0.3, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  ridge(ctx, w, h, rng, { color: t.mid, peaks: 5, min: 0.15, max: 0.32, sharp: false });
}

function synthGrid(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  ridge(ctx, w, h, rng, {
    color: t.mid,
    peaks: 9,
    min: 0.25,
    max: 0.75,
    sharp: true,
    rim: rgba(t.accent, 0.8),
  });
}

const ART: Readonly<Record<BackdropKind, BackdropArt>> = {
  synthwave: {
    far: stars(1),
    mid: synthGrid,
    near: (ctx, w, h, rng, t) =>
      ridge(ctx, w, h, rng, { color: t.near, peaks: 6, min: 0.2, max: 0.45, sharp: false }),
    celestial: 'sun',
  },
  ocean: {
    far: stars(1.1),
    mid: (ctx, w, h, rng, t) => waveBands(ctx, w, h, rng, t.mid, t.glow, 2),
    near: (ctx, w, h, rng, t) => waveBands(ctx, w, h, rng, t.near, t.glow, 2),
    celestial: 'moon',
  },
  city: {
    far: stars(0.45),
    mid: (ctx, w, h, rng, t) => skyline(ctx, w, h, rng, t.mid, t.glow, 0.22),
    near: (ctx, w, h, rng, t) => skyline(ctx, w, h, rng, t.near, t.glow, 0.08),
    celestial: 'glow',
  },
  mountains: {
    far: stars(1.2),
    mid: (ctx, w, h, rng, t) =>
      ridge(ctx, w, h, rng, {
        color: t.mid,
        peaks: 7,
        min: 0.35,
        max: 0.95,
        sharp: true,
        snow: rgba('#e6f0ff', 0.85),
      }),
    near: (ctx, w, h, rng, t) => trees(ctx, w, h, rng, t.near, true, 26),
    celestial: 'moon',
  },
  rain: {
    far: stars(0.35),
    mid: (ctx, w, h, rng, t) => skyline(ctx, w, h, rng, t.mid, t.glow, 0.25),
    near: (ctx, w, h, rng, t) => skyline(ctx, w, h, rng, t.near, null, 0),
    falling: true,
  },
  volcano: {
    far: embers,
    mid: volcanoes,
    near: (ctx, w, h, rng, t) =>
      ridge(ctx, w, h, rng, {
        color: t.near,
        peaks: 10,
        min: 0.2,
        max: 0.6,
        sharp: true,
        rim: rgba('#ff7a1a', 0.5),
      }),
    celestial: 'glow',
  },
  clouds: {
    far: sparkles,
    mid: (ctx, w, h, rng, t) => puffs(ctx, w, h, rng, t.mid, 6, 0.16, '#ffffff'),
    near: (ctx, w, h, rng, t) => puffs(ctx, w, h, rng, t.near, 7, 0.2, '#ffffff'),
  },
  crystals: {
    far: aurora,
    mid: (ctx, w, h, rng, t) => shards(ctx, w, h, rng, t.mid, t.glow, 16, 1),
    near: (ctx, w, h, rng, t) => shards(ctx, w, h, rng, t.near, t.glow, 22, 0.8),
  },
  forest: {
    far: fireflies,
    mid: (ctx, w, h, rng, t) => trees(ctx, w, h, rng, t.mid, false, 22),
    near: (ctx, w, h, rng, t) => trees(ctx, w, h, rng, t.near, true, 30),
    celestial: 'moon',
  },
  planets: {
    far: stars(1.8),
    mid: asteroids,
    near: craters,
    celestial: 'planet',
  },
};

// ---- celestial bodies ----

/** Sun, moon, ringed planet or a glow on the horizon, painted into the sky layer. */
function drawCelestial(
  ctx: CanvasRenderingContext2D,
  kind: NonNullable<BackdropArt['celestial']>,
  theme: Theme,
  w: number,
  h: number,
): void {
  const size = Math.min(h * 0.5, w * 0.32);
  const cx = w * 0.68;
  if (kind === 'glow') {
    const horizon = h * REST_FLOOR;
    const glow = ctx.createRadialGradient(cx, horizon, 0, cx, horizon, size * 1.6);
    glow.addColorStop(0, rgba(theme.glow, 0.5));
    glow.addColorStop(0.5, rgba(theme.glow, 0.16));
    glow.addColorStop(1, rgba(theme.glow, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(cx - size * 1.6, horizon - size * 1.6, size * 3.2, size * 3.2);
    return;
  }
  if (kind === 'sun') {
    const r = size * 0.42;
    const cy = h * 0.52;
    const sun = createLayer(Math.ceil(r * 2) + 2, Math.ceil(r * 2) + 2);
    if (!sun) return;
    const c = sun.canvas.width / 2;
    const gradient = sun.ctx.createLinearGradient(0, c - r, 0, c + r);
    gradient.addColorStop(0, '#fff27a');
    gradient.addColorStop(0.5, '#ff9a3d');
    gradient.addColorStop(1, theme.accent);
    sun.ctx.fillStyle = gradient;
    sun.ctx.beginPath();
    sun.ctx.arc(c, c, r, 0, Math.PI * 2);
    sun.ctx.fill();
    // Retro stripes cut out of the lower half (the sky shows through).
    sun.ctx.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 6; i++) {
      sun.ctx.fillRect(0, c + r * (0.08 + i * 0.16), c * 2, r * (0.03 + i * 0.018));
    }
    ctx.drawImage(sun.canvas, cx - c, cy - c);
    return;
  }
  const cy = h * 0.24;
  if (kind === 'moon') {
    const r = size * 0.2;
    const glow = ctx.createRadialGradient(cx, cy, r * 0.8, cx, cy, size / 2);
    glow.addColorStop(0, rgba(theme.glow, 0.35));
    glow.addColorStop(1, rgba(theme.glow, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
    ctx.fillStyle = mix(theme.glow, '#ffffff', 0.5);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(theme.sky[1], 0.25);
    for (const [dx, dy, rr] of [
      [-0.3, -0.2, 0.22],
      [0.25, 0.1, 0.16],
      [-0.05, 0.35, 0.12],
    ] as const) {
      ctx.beginPath();
      ctx.arc(cx + dx * r, cy + dy * r, rr * r, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
  // Ringed planet.
  const r = size * 0.24;
  const body = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  body.addColorStop(0, '#ffd27a');
  body.addColorStop(1, '#a3328a');
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(-0.35);
  ctx.strokeStyle = rgba('#ffe7a8', 0.75);
  ctx.lineWidth = size * 0.025;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.9, r * 0.45, 0, Math.PI, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = rgba('#ffffff', 0.18);
  ctx.lineWidth = size * 0.012;
  for (const band of [-0.4, 0, 0.35]) {
    ctx.beginPath();
    ctx.moveTo(-r * Math.sqrt(1 - band * band), band * r);
    ctx.lineTo(r * Math.sqrt(1 - band * band), band * r);
    ctx.stroke();
  }
  ctx.strokeStyle = rgba('#ffe7a8', 0.85);
  ctx.lineWidth = size * 0.025;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.9, r * 0.45, 0, 0, Math.PI);
  ctx.stroke();
  ctx.restore();
}
